/* ==========================================================================
   SKIN ANALYSIS — page behaviour.
   Independent IIFEs; each one no-ops if its markup is absent. With reduced
   motion or no IntersectionObserver everything is simply shown.
   ========================================================================== */

(function () {
  'use strict';
  var root = document.querySelector('.sap');
  if (!root) return;

  var reduce =window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canObserve = 'IntersectionObserver' in window;
  if (reduce || !canObserve) return;
  root.classList.add('sap--js');

  /* --- 1. scroll reveals ---------------------------------------------- */
  var revealIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      revealIo.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  root.querySelectorAll('.sap__rv').forEach(function (el) { revealIo.observe(el); });

  /* Iris and wipe start fully clipped, which the observer counts as zero
     area, so they are triggered by their parent instead. */
  var clipIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.querySelectorAll(':scope > .sap__iris, :scope > .sap__wipe').forEach(function (el) { el.classList.add('is-in'); });
      clipIo.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
  root.querySelectorAll('.sap__iris, .sap__wipe').forEach(function (el) { clipIo.observe(el.parentNode); });

  /* --- 2. hero title, letter by letter --------------------------------- */
  root.querySelectorAll('[data-sap-letters]').forEach(function (el) {
    var text = el.textContent.trim();
    el.setAttribute('aria-label', text);
    el.textContent = '';
    /* Letters are grouped per word so a line never breaks inside a word. */
    var i = 0;
    text.split(/\s+/).forEach(function (word, w) {
      if (w) el.appendChild(document.createTextNode(' '));
      var group = document.createElement('span');
      group.className = 'sap-word';
      group.setAttribute('aria-hidden', 'true');
      Array.from(word).forEach(function (ch) {
        var span = document.createElement('span');
        span.className = 'sap-ch';
        span.style.setProperty('--i', i++);
        span.textContent = ch;
        group.appendChild(span);
      });
      el.appendChild(group);
    });
  });

  /* --- 3. words that light up with scroll ------------------------------ */
  var wordBlocks = [];
  root.querySelectorAll('[data-sap-words]').forEach(function (el) {
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    var spans = words.map(function (w, i) {
      var s = document.createElement('span');
      s.className = 'sap-w';
      s.textContent = w;
      el.appendChild(s);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return s;
    });
    wordBlocks.push({ el: el, spans: spans });
  });

  /* --- 4. scroll-linked bits: words, hero parallax --------------------- */
  var hero = root.querySelector('[data-sap-parallax]');
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var vh = window.innerHeight;
      wordBlocks.forEach(function (b) {
        var r = b.el.getBoundingClientRect();
        /* 0 when the block's top reaches 85% of the screen, 1 when its bottom reaches 45% */
        var p = (vh * 0.85 - r.top) / (r.height + vh * 0.4);
        p = Math.max(0, Math.min(1, p));
        var lit = Math.round(p * b.spans.length);
        b.spans.forEach(function (s, i) { s.classList.toggle('is-lit', i < lit); });
      });
      if (hero) {
        var hr = hero.getBoundingClientRect();
        if (hr.bottom > 0) hero.style.setProperty('--sap-par', Math.max(0, -hr.top / hr.height).toFixed(3));
      }
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  onScroll();

  /* --- 5. count-up statistics ------------------------------------------ */
  /* Animates the first number in the text and keeps the locale's own
     separators and words ("1,8 million", "1.500+"). */
  var countIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      countIo.unobserve(entry.target);
      var el = entry.target;
      var text = el.textContent;
      var m = text.match(/\d+(?:[.,\s ]\d+)*/);
      if (!m) return;
      var raw = m[0];
      var dec = raw.match(/^\d+[.,](\d{1,2})$/);
      var decSep = dec ? raw.charAt(raw.length - dec[1].length - 1) : '';
      var groupSep = !dec && raw.match(/\d([.,\s ])\d{3}/) ? raw.match(/\d([.,\s ])\d{3}/)[1] : '';
      var target = parseFloat(raw.replace(/[^\d.,]/g, '').replace(groupSep, '').replace(',', '.'));
      var places = dec ? dec[1].length : 0;
      var start = null;
      var dur = 1800;
      function fmt(v) {
        var s = v.toFixed(places);
        if (places) return s.replace('.', decSep);
        return groupSep ? s.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : s;
      }
      function frame(t) {
        if (start === null) start = t;
        var k = Math.min(1, (t - start) / dur);
        var eased = 1 - Math.pow(1 - k, 4);
        el.textContent = text.replace(raw, fmt(target * eased));
        if (k < 1) requestAnimationFrame(frame); else el.textContent = text;
      }
      requestAnimationFrame(frame);
    });
  }, { threshold: 0.6 });
  root.querySelectorAll('[data-sap-count]').forEach(function (el) { countIo.observe(el); });

  /* --- 6. how it works: the stage follows the step in view ------------- */
  var stage = root.querySelector('[data-sap-stage]');
  var steps = root.querySelectorAll('[data-sap-step]');
  if (steps.length) {
    var stepIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        steps.forEach(function (s) { s.classList.toggle('is-current', s === entry.target); });
        if (stage) stage.setAttribute('data-step', entry.target.getAttribute('data-sap-step'));
      });
    }, { rootMargin: '-45% 0px -45% 0px' });
    steps.forEach(function (s) { stepIo.observe(s); });
    steps[0].classList.add('is-current');
  }

  /* --- 7. the colour wheel --------------------------------------------- */
  var wheelWrap = root.querySelector('[data-sap-wheel]');
  if (wheelWrap) {
    var ring = wheelWrap.querySelector('.sap-wheel');
    var num = wheelWrap.querySelector('.sap-wheel__num');
    var name = wheelWrap.querySelector('.sap-wheel__name');
    var swatches = wheelWrap.querySelectorAll('[data-sap-swatch]');
    var hub = wheelWrap.querySelector('.sap-wheel__hub');
    var photos = wheelWrap.querySelectorAll('[data-sap-photo]');
    var current = -1;
    var turns = 0;

    var activate = function (idx) {
      if (idx === current) return;
      /* Turn the short way round so the wheel never spins backwards a full lap. */
      var prev = current < 0 ? 0 : current;
      var delta = ((idx - prev) % 8 + 8) % 8;
      if (delta > 4) delta -= 8;
      turns += delta;
      current = idx;
      ring.style.setProperty('--rot', (turns * -45) + 'deg');
      var sw = swatches[idx];
      swatches.forEach(function (s) { s.classList.toggle('is-active', s === sw); });
      num.textContent = (idx < 9 ? '0' : '') + (idx + 1);
      name.textContent = sw.querySelector('.sap-swatch__name').textContent;
      var hasPhoto = false;
      photos.forEach(function (p) {
        var on = Number(p.getAttribute('data-sap-photo')) === idx;
        p.classList.toggle('is-on', on);
        if (on) hasPhoto = true;
      });
      hub.classList.toggle('has-photo', hasPhoto);
      name.style.setProperty('--sap-active', getComputedStyle(sw).getPropertyValue('--c'));
      name.classList.remove('is-swap');
      void name.offsetWidth; /* restart the swap animation */
      name.classList.add('is-swap');
    };
    activate(0);

    swatches.forEach(function (sw, i) {
      sw.addEventListener('mouseenter', function () { activate(i); });
      sw.addEventListener('focus', function () { activate(i); });
      sw.addEventListener('click', function () { activate(i); });
    });
    var swIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) activate(Number(entry.target.getAttribute('data-sap-swatch')));
      });
    }, { rootMargin: '-48% 0px -48% 0px' });
    swatches.forEach(function (sw) { swIo.observe(sw); });
  }

  /* --- 8. chapter nav highlights the chapter in view ------------------- */
  var chips = root.querySelectorAll('[data-sap-chip]');
  var chapters = root.querySelectorAll('[data-sap-chapter]');
  if (chips.length && chapters.length) {
    var chIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var id = '#' + entry.target.id;
        chips.forEach(function (c) {
          var on = c.getAttribute('href') === id;
          c.classList.toggle('is-current', on);
          if (on && c.scrollIntoView && window.innerWidth < 750) {
            c.parentNode.scrollTo({ left: c.offsetLeft - 16, behavior: 'smooth' });
          }
        });
      });
    }, { rootMargin: '-40% 0px -55% 0px' });
    chapters.forEach(function (c) { chIo.observe(c); });
  }
})();
